import React, { useRef, useState } from "react";
import { axiosConfig } from "../../../axios/axiosConfig";
import toast from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";
import AdvisoryDetailsTable from "./AdvisoryDetailsTable";
import StockAdvisoryForm from "./StockAdvisoryForm";

import styles from "./AdvisorHome.module.css";
import OneSignals from "../../../pages/One SIgnals";
import { Box, Typography, Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CloseIcon from "@mui/icons-material/Close";

const AdvisorHome = () => {
  const [advisoryStocksList, setAdvisoryStocksList] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [activeAdvisoryObj, setActiveAdvisoryObj] = useState({});
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const exitPriceRef = useRef(null);

  const initialAdvisoryFormInput = {
    symbolName: "",
    advisoryType: "MEDIUM",
    priceRange: "",
    entryPrice: "", // suggested price, NO RUPEE symbols in entry, target and stop loss
    targetPrice: "",
    stopLoss: "",
    description: "",
    exitPrice: "N/A", // EXIT price is only visible for EDIT / UPDATE
    profitLoss: "",
    tradeClosedInDays: "",
    trade_status: "OPEN", // OPEN, CLOSED, CANCEL or INACTIVE
    cancel_reason: "",
    call_type: "BID", // BID or CMP
    bid_type: "",
    valid_for_parrva: false,
    parrva_call_type: "TECHNICAL",
    stock_horizon: "",
  };
  const [advisoryFormInput, setAdvisoryFormInput] = useState(
    initialAdvisoryFormInput
  );

  // CLEAR / RESET advisoryFormInput
  const clearAdvisoryFormInput = () => {
    setAdvisoryFormInput(initialAdvisoryFormInput);
  };

  // FETCHING ADVISORY DETAILS
  const advisoryQuery = useQuery({
    retry: false,
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchInterval: false, // Disable automatic refetching
    queryKey: ["advisory-details"], // dependency
    queryFn: () => fetchAdvisoryDetails(),
    onSuccess: (data) => {
      setAdvisoryStocksList(data.response?.data);
    },
    onError: (error) => {
      // console.log(" error: ", error);
      toast.error(error?.response?.data?.response || "Something went wrong");
    },
  });

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setIsEditing(false);
    clearAdvisoryFormInput();
  };

  // this function copies the object row in advisorytable to AdvisoryFormInput, makes it active
  const handleEditBtn = (item_obj) => {
    const {
      symbol_name,
      advisory_type,
      price_range,
      entry_price,
      target_price,
      stop_loss,
    } = item_obj;
    const tempAdvisoryFormInput = {
      symbolName: symbol_name,
      advisoryType: advisory_type?.toUpperCase(),
      priceRange: price_range,
      entryPrice: entry_price, // suggested price, NO RUPEE symbols in entry, target and stop loss
      targetPrice: target_price,
      stopLoss: stop_loss,
      description: item_obj?.description,
      trade_status: item_obj?.trade_status || "OPEN",
      cancel_reason: item_obj?.cancel_reason || "",
      exitPrice: item_obj?.exit_price || "",
      valid_for_parrva: !!item_obj?.valid_for_parrva,
      parrva_call_type: item_obj?.parrva_call_type || "",
      stock_horizon: item_obj?.stock_horizon || "",
    };

    setIsEditing(true);
    setActiveAdvisoryObj(item_obj);
    setAdvisoryFormInput(tempAdvisoryFormInput);
    setIsFormModalOpen(true);
  };

  const handleCancelBtn = (item_obj) => {
    handleEditBtn(item_obj);
    setAdvisoryFormInput((prev) => ({ ...prev, trade_status: "CANCEL" }));
  };

  const handleExitBtn = (item_obj) => {
    handleEditBtn(item_obj);
    setAdvisoryFormInput((prev) => ({ ...prev, trade_status: "CLOSED", exitPrice: "" }));
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          padding: "16px 24px",
          marginBottom: "24px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
        }}
      >
        <TrendingUpIcon sx={{ color: "#b8d4c4", fontSize: 28 }} />
        <Box>
          <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
            Stock Advisory
          </Typography>
          <Typography sx={{ color: "#b8d4c4", fontSize: 12 }}>
            Manage and publish stock advisory calls
          </Typography>
        </Box>

        <button
          onClick={() => { clearAdvisoryFormInput(); setIsEditing(false); setActiveAdvisoryObj({}); setIsFormModalOpen(true); }}
          style={{
            marginLeft: "auto",
            padding: "9px 22px",
            background: "#016551",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "14px",
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "linear-gradient(135deg, #1db954 0%, #17a349 100%)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#016551"; }}
        >
          + Create Advisory
        </button>
      </Box>

      {/* Form Dialog */}
      <Dialog
        open={isFormModalOpen}
        onClose={handleCloseFormModal}
        maxWidth="lg"
        fullWidth
        sx={{ "& .MuiDialog-paper": { maxHeight: "92vh", borderRadius: "12px", overflow: "hidden" } }}
      >
        <DialogTitle sx={{ background: "#04343a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5, px: 3 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{isEditing ? "Edit Advisory" : "Create Advisory"}</Typography>
          <IconButton onClick={handleCloseFormModal} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, overflowY: "auto", background: "#eef2f7" }}>
          <StockAdvisoryForm
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            advisoryFormInput={advisoryFormInput}
            setAdvisoryFormInput={setAdvisoryFormInput}
            initialAdvisoryFormInput={initialAdvisoryFormInput}
            clearAdvisoryFormInput={clearAdvisoryFormInput}
            activeAdvisoryObj={activeAdvisoryObj}
            exitPriceRef={exitPriceRef}
            onSuccess={handleCloseFormModal}
          />
        </DialogContent>
      </Dialog>

      {/* advisory details table */}
      <AdvisoryDetailsTable
        advisoryDetailsList={advisoryStocksList}
        handleEditBtn={handleEditBtn}
        handleCancelBtn={handleCancelBtn}
        handleExitBtn={handleExitBtn}
        refreshAdvisory={() => advisoryQuery.refetch()}
      />
    </Box>
  );
};

export default AdvisorHome;

// UTILITIES
// fetching all stocks advisory (open or closed)
const fetchAdvisoryDetails = async () => {
  // if (!watchlistId) return;

  const res = await axiosConfig({
    method: "get",
    // url: "/predictions/stocks/advisory?status=OPEN",
    url: "/predictions/stocks/advisory",
    data: {},
  });

  return res.data;
};
